import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import XmasText from '~/assets/holiday/christmas/christmasText.png';
import XmasBgDesktop from '~/assets/holiday/christmas/login/bgDesktop.png';
import XmasBgMobile from '~/assets/holiday/christmas/login/bgMobile.png';
import XmasRobotMobile from '~/assets/holiday/christmas/login/robotMobile.png';
import XmasRobotDesktop from '~/assets/holiday/christmas/robotDesktop.png';
import Logo from '~/assets/holiday/logo.png';
import MdBg from '~/assets/holiday/memorial-day/bg.png';
import MdBottomWidget from '~/assets/holiday/memorial-day/bottomWidget.png';
import MdText from '~/assets/holiday/memorial-day/login/memorialDay.svg';
import MdTopLeftWidget from '~/assets/holiday/memorial-day/login/topLeftWidget.png';
import NyBgDesktop from '~/assets/holiday/new-year/login/bgDesktop.png';
import NyBgMobile from '~/assets/holiday/new-year/login/bgMobile.png';
import NyText from '~/assets/holiday/new-year/newYearText.png';
import TgBgDesktop from '~/assets/holiday/thanksgiving/login/bgDesktop.png';
import TgBgMobile from '~/assets/holiday/thanksgiving/login/bgMobile.png';
import TgBottomLeftWidget from '~/assets/holiday/thanksgiving/login/bottomLeftWidget.png';
import TgBottomRightWidget from '~/assets/holiday/thanksgiving/login/bottomRightWidget.png';
import TgLogo from '~/assets/holiday/thanksgiving/login/logo.png';
import TgRobot from '~/assets/holiday/thanksgiving/login/robot.png';
import TgText from '~/assets/holiday/thanksgiving/login/thanksgivingText.png';
import TgTopLeftWidget from '~/assets/holiday/thanksgiving/login/topLeftWidget.png';
import TgTopRightWidget from '~/assets/holiday/thanksgiving/login/topRightWidget.png';
import IconNetwork from '~/assets/images/identity/network.png';
import IconRobot from '~/assets/images/identity/robotWand.png';
import { ChristmasMenu } from '~/components/layouts/Menu/ChristmasMenu';
import { MemorialDayMenu } from '~/components/layouts/Menu/MemorialDayMenu';
import { Menu } from '~/components/layouts/Menu/Menu';
import { NewYearMenu } from '~/components/layouts/Menu/NewYearMenu';
import { ThanksgivingMenu } from '~/components/layouts/Menu/ThanksgivingMenu';
import { getPublicFileURL } from './prefix';

export interface ThemeElement {
  tag: 'img' | 'h1' | 'p' | 'div';
  className?: string;
  style?: Record<string, string>;

  path?: string;
  alt?: string;

  content?: string;
  children?: ThemeElement[];
}

export interface Login {
  component?: any;
  containerClass?: string;
  containerStyle?: Record<string, string>;
  formContainerClass?: string;
  elements: ThemeElement[];
}

export interface Menu {
  component?: any;
}

export interface HolidayTheme {
  name: string;
  dateRange?: { start: string; end: string; isYearSpanning?: boolean };
  login: Login;
  menu: Menu;
}

// ---------------------------------------------------------------------------
//  Shared layout classes
// ---------------------------------------------------------------------------

const HOLIDAY_FORM_CLASS =
  'flex w-11/12 flex-col justify-center rounded-xl bg-white px-5 pb-8 pt-6 shadow-lg lg:h-full lg:min-w-[50vw] lg:max-w-[50vw] lg:flex-1 lg:items-center lg:rounded-none lg:shadow-none';

const HERO_SECTION_CLASS =
  'relative overflow-hidden flex lg:bg-[image:var(--bg-desktop)] bg-cover bg-center bg-no-repeat flex-col items-center px-3 lg:order-2 min-h-[250px] lg:h-full w-full justify-center';

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

function createHolidayLogin(config: { bgColor: string; mobileBg: string; desktopBg: string; children: ThemeElement[] }): Login {
  return {
    containerClass: `relative ${config.bgColor} max-lg:bg-[image:var(--bg-mobile)] flex h-screen min-h-fit w-full flex-col items-center bg-cover bg-center bg-no-repeat pb-8 lg:flex-row lg:pb-0`,
    containerStyle: { '--bg-mobile': `url(${config.mobileBg})` },
    formContainerClass: HOLIDAY_FORM_CLASS,
    elements: [
      {
        tag: 'div',
        className: HERO_SECTION_CLASS,
        style: { '--bg-desktop': `url(${config.desktopBg})` },
        children: config.children,
      },
    ],
  };
}

function isDateInRange(month: number, day: number, range: NonNullable<HolidayTheme['dateRange']>): boolean {
  const [startMonth, startDay] = range.start.split('-').map(Number);
  const [endMonth, endDay] = range.end.split('-').map(Number);

  const afterStart = month > startMonth || (month === startMonth && day >= startDay);
  const beforeEnd = month < endMonth || (month === endMonth && day <= endDay);

  return range.isYearSpanning ? afterStart || beforeEnd : afterStart && beforeEnd;
}

// ---------------------------------------------------------------------------
//  Theme definitions
// ---------------------------------------------------------------------------

const DEFAULT_THEME: HolidayTheme = {
  name: 'Default',
  login: {
    containerClass:
      'relative flex h-screen min-h-fit w-full flex-col items-center justify-center bg-contain bg-right bg-no-repeat pb-8 pt-10 lg:flex-row lg:py-0',
    containerStyle: {
      'background-image': `url(${IconNetwork})`,
    },
    formContainerClass:
      'flex w-11/12 max-w-[516px] flex-col justify-center rounded-xl bg-white px-5 pb-8 pt-6 shadow-lg lg:h-full lg:min-w-[50vw] lg:max-w-[50vw] lg:flex-1 lg:items-center lg:rounded-none lg:shadow-none',
    elements: [
      {
        tag: 'div',
        className:
          'mb-7 flex max-w-[308px] flex-col items-center px-3 md:mb-20 md:max-w-[476px] lg:order-2 lg:mb-0 lg:h-full lg:min-w-[20vw] lg:max-w-[50vw] lg:flex-1 lg:justify-center',
        children: [
          {
            tag: 'img',
            path: getPublicFileURL('/logoFull.png'),
            alt: 'Logo',
            className: 'absolute right-5 top-5 hidden h-20 lg:block h-[40px]',
          },
          {
            tag: 'img',
            path: IconRobot,
            alt: 'Robot',
            className: 'mx-auto mb-2 w-[142px] md:mb-6 md:w-[263px]',
          },
          {
            tag: 'h1',
            content: 'Time is magic',
            className: 'mb-1 text-center text-lg font-bold uppercase text-white md:mb-2 md:text-[28px]',
          },
          {
            tag: 'p',
            content: 'AI-automated rental management that simplifies your operations',
            className: 'text-center text-xs text-white/70 md:text-xl lg:max-w-[476px]',
          },
        ],
      },
    ],
  },
  menu: {
    component: Menu,
  },
};

const thanksgivingTheme: HolidayTheme = {
  name: 'Thanksgiving',
  dateRange: { start: '11-15', end: '11-30' },
  login: createHolidayLogin({
    bgColor: 'bg-[#F9F4DB]',
    mobileBg: TgBgMobile,
    desktopBg: TgBgDesktop,
    children: [
      {
        tag: 'img',
        path: TgTopLeftWidget,
        className: 'lg:w-[144px] w-[67px] absolute top-[-3px] left-[-3px]',
      },
      {
        tag: 'img',
        path: TgTopRightWidget,
        className: 'lg:w-[144px] w-[67px] absolute top-[-3px] right-[-3px]',
      },
      {
        tag: 'img',
        path: TgText,
        className: 'lg:w-[499px] w-[194px]',
      },
      {
        tag: 'div',
        className: 'lg:w-[415px] w-[176px] absolute bottom-0 left-0',
        children: [
          {
            tag: 'img',
            path: TgBottomLeftWidget,
            className: 'w-full',
          },
          {
            tag: 'div',
            className:
              'absolute lg:hidden bottom-0 left-0 w-[135px] h-[29px] bg-[linear-gradient(180deg,rgba(249,243,218,0)_0%,#F9F3DA_100%)] z-10',
          },
        ],
      },
      {
        tag: 'img',
        path: TgBottomRightWidget,
        className: 'w-[169px] hidden lg:block absolute bottom-0 right-0',
      },
      {
        tag: 'img',
        path: TgRobot,
        className: 'lg:w-[251px] w-[110px] absolute bottom-0 lg:right-[129px] right-0',
      },
      {
        tag: 'img',
        path: TgLogo,
        className: 'w-[243px] hidden lg:block absolute top-[40px]',
      },
    ],
  }),
  menu: {
    component: ThanksgivingMenu,
  },
};

const christmasTheme: HolidayTheme = {
  name: 'Christmas',
  dateRange: { start: '12-01', end: '12-29' },
  login: createHolidayLogin({
    bgColor: 'bg-white',
    mobileBg: XmasBgMobile,
    desktopBg: XmasBgDesktop,
    children: [
      {
        tag: 'img',
        path: XmasText,
        className: 'lg:w-[418px] w-[231px] lg:mb-20',
      },
      {
        tag: 'img',
        path: XmasRobotDesktop,
        className: 'w-[305px] hidden lg:block absolute bottom-0 left-20',
      },
      {
        tag: 'img',
        path: XmasRobotMobile,
        className: 'w-[68px] block lg:hidden absolute -bottom-4 right-12',
      },
      {
        tag: 'img',
        path: Logo,
        className: 'w-[179px] hidden lg:block absolute top-[40px]',
      },
    ],
  }),
  menu: {
    component: ChristmasMenu,
  },
};

const newYearTheme: HolidayTheme = {
  name: 'NewYear',
  dateRange: { start: '12-30', end: '1-10', isYearSpanning: true },
  login: createHolidayLogin({
    bgColor: 'bg-white',
    mobileBg: NyBgMobile,
    desktopBg: NyBgDesktop,
    children: [
      {
        tag: 'img',
        path: NyText,
        className: 'lg:w-[348px] w-[231px] max-lg:mt-12',
      },
    ],
  }),
  menu: {
    component: NewYearMenu,
  },
};

const memorialDayTheme: HolidayTheme = {
  name: 'MemorialDay',
  dateRange: { start: '5-21', end: '5-26' },
  login: createHolidayLogin({
    bgColor: 'bg-[#0F2A63]',
    mobileBg: MdBg,
    desktopBg: MdBg,
    children: [
      {
        tag: 'img',
        path: MdText,
        className: 'lg:w-[249px] w-[170px] lg:mb-20',
      },
      {
        tag: 'img',
        path: MdTopLeftWidget,
        className: 'lg:w-[569px] w-[235px] absolute top-0 left-0',
      },
      {
        tag: 'img',
        path: MdBottomWidget,
        className: 'w-full lg:block hidden absolute bottom-0 right-0',
      },
      {
        tag: 'img',
        path: Logo,
        className: 'w-[157px] hidden lg:block absolute top-[40px] right-[40px]',
      },
    ],
  }),
  menu: {
    component: MemorialDayMenu,
  },
};

// ---------------------------------------------------------------------------
//  Exports
// ---------------------------------------------------------------------------

export const HOLIDAY_THEMES: HolidayTheme[] = [thanksgivingTheme, christmasTheme, newYearTheme, memorialDayTheme];

export function getCurrentTheme(): HolidayTheme {
  const now = todayWithSystemTimeZone().toDate();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  return HOLIDAY_THEMES.find((theme) => theme.dateRange && isDateInRange(month, day, theme.dateRange)) ?? DEFAULT_THEME;
}
